import { NativeGeocoder } from '@awesome-cordova-plugins/native-geocoder';
import BackgroundGeolocation, { Location } from '@transistorsoft/capacitor-background-geolocation';
import moment from 'moment';
import axiosInstance from '../api/axiosInstance';
import { enableGeolocationLogRef } from '../App';
import { IGeocodedAddress } from '../models/dms/address.model';
import { IGeocoderResult, IGeolocation, ISnappedPointsContainer } from '../models/geolocation.model';
import { LogType } from '../models/log.model';
import { logTransaction } from './log';

const interpolate = 'true';

export const getAccurateDistance = async (location: IGeolocation[]) => {
  try {
    const snappedLocations = await getAccurateLocations(location);
    const distance = getTotalDistance(snappedLocations);
    return distance;
  } catch (ex: any) {
    throw ex;
  }
};

export const getAccurateLocations = async (location: IGeolocation[]) => {
  let uniqueLocations = getUniqueLocations(location);
  try {
    const snappedLocations = [...uniqueLocations];

    //const snappedLocationsRaw = await snapLocationsToRoad(uniqueLocations);
    // let snappedLocations = [] as IGeolocation[];
    // snappedLocationsRaw.map((snappedPoints) => {
    //     // Record positions
    //     snappedPoints.map((point, index) => {
    //         snappedLocations.push({ ...point, time: point.time } as IGeolocation);
    //     });
    // });

    return snappedLocations;
  } catch (ex: any) {
    throw ex;
  }
};

export const getUniqueLocations = (locations: IGeolocation[]) => {
  const locationsReversed = [...locations].reverse();
  const ids = locationsReversed.map((l) => `${l.latitude},${l.longitude}`);
  const filtered = locationsReversed.filter((l, index) => !ids.includes(`${l.latitude},${l.longitude}`, index + 1));
  return filtered.reverse();

  /*
      Array.filter() removes all duplicate objects by checking if the previously mapped id-array includes the current id.
      To only filter out actual duplicates, use Array.includes() second parameter fromIndex with index + 1 which will ignore the current object and all previous.
      Since every iteration of the filter callback method will only search the array beginning at the current index + 1, this also dramatically reduces the runtime because
      only objects not previously filtered get checked.
      The array is reversed because location.filter looks forwards for duplicates and doing so without reversing the array would result in the first occurrence of a duplicate
      being removed instead of the last occurrence.
    */
};

export const snapLocationsToRoad = async (locations: IGeolocation[]): Promise<IGeolocation[][]> => {
  // Split into maximum of 100 locations at a time
  // 0=0-99, 1=100-199, etc
  const locationsSplit = [] as Promise<IGeolocation[]>[];
  const locationSplitVals = Math.ceil(locations.length / 100);
  for (let i = 0; i < locationSplitVals; i++) {
    const newLocations = locations.slice(i * 100, (i + 1) * 100);

    const pathString = newLocations
      .reduce((val, path) => {
        val.push(path.latitude + ',' + path.longitude);
        return val;
      }, [] as string[])
      .join('|');

    locationsSplit.push(
      new Promise<IGeolocation[]>(async (res, err) => {
        try {
          const response = await axiosInstance.get<ISnappedPointsContainer>(`https://roads.googleapis.com/v1/snapToRoads?path=${pathString}&interpolate=${interpolate}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`, {
            timeout: 30000,
          });
          try {
            const geolocations = [] as IGeolocation[];
            response.data.snappedPoints.forEach((point) => {
              let geolocation = { ...point.location } as IGeolocation;

              if (point.originalIndex !== undefined) {
                geolocation = { ...newLocations[point.originalIndex], ...geolocation };
              }

              geolocations.push(geolocation);
            });
            res(geolocations);
          } catch (ex) {
            err(ex);
          }
        } catch (ex: any) {
          logTransaction(LogType.Error, `Caught error calling snapToRoads endpoint - error: ${ex?.message}`);
          err(ex);
          throw ex;
        }
      }),
    );
  }

  return Promise.all<IGeolocation[]>(locationsSplit);
};

export const getApiGeocode = async (location: IGeolocation): Promise<IGeocoderResult[]> => {
  try {
    const response = await axiosInstance.get<any>(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.latitude},${location.longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`);
    enableGeolocationLogRef && logTransaction(LogType.Info, 'Captured API reverse geocode data', response.data.results);
    const data = response.data.results as IGeocoderResult[];
    return data;
  } catch (ex: any) {
    logTransaction(LogType.Error, `Caught error calling getApiGeocode endpoint - error: ${ex?.message}`);
    throw ex;
  }
};

export const getNativeGeocode = async (location: IGeolocation) => {
  try {
    const geocodeResults = await NativeGeocoder.reverseGeocode(location.latitude, location.longitude);
    enableGeolocationLogRef && logTransaction(LogType.Info, 'Captured native reverse geocode data', geocodeResults);
    const firstGeocode = geocodeResults[0];

    let addressData = {
      city: firstGeocode.locality,
      streetName: firstGeocode.thoroughfare,
    } as IGeocodedAddress;

    return addressData;
  } catch (ex: any) {
    logTransaction(LogType.Error, `Caught error calling getNativeGeocode endpoint - error: ${ex?.message}`);
    throw ex;
  }
};

export const getTotalDistance = (coordinates: IGeolocation[]): number => {
  coordinates = coordinates.filter((cord) => {
    if (cord.latitude && cord.longitude) {
      return true;
    }
    return false;
  });

  let totalDistance = 0;

  if (!coordinates) {
    return 0;
  }

  if (coordinates.length < 2) {
    return 0;
  }

  for (let i = 0; i < coordinates.length - 2; i++) {
    if (!coordinates[i].longitude || !coordinates[i].latitude || !coordinates[i + 1].longitude || !coordinates[i + 1].latitude) {
      continue;
    }
    totalDistance = totalDistance + getDistanceBetweenTwoPoints(coordinates[i], coordinates[i + 1]);
  }

  return Number.parseFloat(totalDistance.toFixed(2));
};

export const getDistanceBetweenTwoPoints = (cord1: IGeolocation, cord2: IGeolocation) => {
  if (cord1.latitude === cord2.latitude && cord1.longitude === cord2.longitude) {
    return 0;
  }

  const earthRadius = 6371000;

  const dLat = degreesToRadians(cord2.latitude - cord1.latitude);
  const dLon = degreesToRadians(cord2.longitude - cord1.longitude);

  const lat1 = degreesToRadians(cord1.latitude);
  const lat2 = degreesToRadians(cord2.latitude);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const degreesToRadians = (degrees: number) => {
  return (degrees * Math.PI) / 180;
};

export const getFakeLocation = () => {
  const fakeLocations = [
    { latitude: -37.7336874, longitude: 144.9768448, accuracy: Math.floor(Math.random() * 41) },
    { latitude: -37.7996948, longitude: 145.060443, accuracy: Math.floor(Math.random() * 41) },
    { latitude: -37.8169149, longitude: 145.167766, accuracy: Math.floor(Math.random() * 41) },
    { latitude: -37.81687, longitude: 145.1667954, accuracy: Math.floor(Math.random() * 41) },
    { latitude: -37.8128864, longitude: 145.159676, accuracy: Math.floor(Math.random() * 41) },
    { latitude: -37.7319917, longitude: 144.7334393, accuracy: Math.floor(Math.random() * 41) },
  ] as IGeolocation[];
  const randomSelection = fakeLocations[Math.floor(Math.random() * fakeLocations.length)];
  return randomSelection;
};

export const moveTowards = (lat: number, lng: number, lat2: number, lng2: number, distanceToMove: number) => {
  // Moves the given distance from original lat/long towards new lat/lng returning the resultant lat/lng
  const earthRadius = 6371000;
  const latDist = lat - lat2;
  const lngDist = lng - lng2;
  const latDistRad = degreesToRadians(latDist);
  const lngDistRad = degreesToRadians(lngDist);
  const sinLatD = Math.sin(latDistRad);
  const sinLngD = Math.sin(lngDistRad);
  const cosLat1 = Math.cos(degreesToRadians(lat));
  const cosLat2 = Math.cos(degreesToRadians(lat2));
  const a = Math.abs((sinLatD / 2) * (sinLatD / 2) + cosLat1 * cosLat2 * (sinLngD / 2) * (sinLngD / 2));
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadius * c;

  const ratio = distanceToMove / distance;

  const newLat = lat + (lat2 - lat) * ratio;
  const newLng = lng + (lng2 - lng) * ratio;

  return {
    latitude: newLat,
    longitude: newLng,
  } as IGeolocation;
};

export const moveDistance = (lat: number, lng: number, dn: number, de: number) => {
  const earthRadius = 6371000;

  //Coordinate offsets in radians
  const dLat = dn / earthRadius;
  const dLon = de / (earthRadius * Math.cos((Math.PI * lat) / 180));

  //OffsetPosition, decimal degrees
  const latO = lat + (dLat * 180) / Math.PI;
  const lonO = lng + (dLon * 180) / Math.PI;

  return {
    latitude: latO,
    longitude: lonO,
  } as IGeolocation;
};

export const getPosition = (location: Location) => {
  return {
    latitude: location.coords.latitude,
    longitude: location.coords.longitude,
    accuracy: location.coords.accuracy,
    time: moment(location.timestamp, moment.ISO_8601).toDate().getTime(),
  } as IGeolocation;
};

export const getLocations = async (allowInaccurate: boolean, allowStale: boolean) => {
  logTransaction(LogType.Info, `Getting RAW location data`);

  try {
    const locations = (await BackgroundGeolocation.getLocations()) as Location[];
    const locationsClensed = locations.reduce((prev, curr) => {
      // Remove points with low accuracy
      const location = getLocation(curr, allowInaccurate, allowStale);
      if (location) {
        prev.push(location);
      }

      return prev;
    }, [] as IGeolocation[]);

    return locationsClensed;
  } catch {}

  return [];
};

export const getLocation = (location: Location, allowInaccurate: boolean, allowStale: boolean) => {
  const acceptableAccuracy = 200; // Acceptable accuracy in metres
  const acceptableTimestampTheshold = 5; // Acceptable timestamp theshold in seconds (filters stale locations)
  const position = getPosition(location);
  const positionString = getPositionString(location);
  const currentTimeUtc = moment().utcOffset(0);

  // Accuracy
  const isAccurate = allowInaccurate ? true : position.accuracy <= acceptableAccuracy;

  // Stale
  const diff = currentTimeUtc.diff(moment(position.time), 'seconds');
  const isStale = allowStale ? false : diff > acceptableTimestampTheshold;

  if (isAccurate && !isStale) {
    return position;
  } else {
    if (!isAccurate) {
      logTransaction(LogType.Warn, `Location exceeds max ${acceptableAccuracy}m: ${positionString}`);
    }
    if (isStale) {
      logTransaction(LogType.Warn, `Location timestamp ${location.timestamp} is stale by ${diff}s because it exceeds ${acceptableTimestampTheshold}s for current timestamp ${currentTimeUtc.toISOString()}`);
    }
  }

  return undefined;
};

export const getLocationsLog = async () => {
  try {
    return await BackgroundGeolocation.logger.getLog();
  } catch {}
  return '';
};

export const clearLocationsLog = async () => {
  try {
    await BackgroundGeolocation.logger.destroyLog();
  } catch {}
};

export const getLocationOdomoeter = async () => {
  try {
    return await BackgroundGeolocation.getOdometer();
  } catch {}
  return 0;
};

export const setLocationOdometer = async (val: number) => {
  try {
    await BackgroundGeolocation.setOdometer(val);
  } catch {}
};

export const setLocationLogLevel = async (verbose: boolean) => {
  try {
    await BackgroundGeolocation.setConfig({
      logLevel: verbose ? BackgroundGeolocation.LOG_LEVEL_VERBOSE : BackgroundGeolocation.LOG_LEVEL_ERROR,
    });
  } catch {}
};

export const destroyLocations = async () => {
  try {
    await BackgroundGeolocation.destroyLocations();
  } catch {}
};

export const getPositionString = (location: Location) => {
  return `${location.coords.latitude.toFixed(8)},${location.coords.longitude.toFixed(8)},${location.coords.accuracy.toFixed(2)}`;
};
